import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  BookOpen, 
  Clock, 
  ChevronRight,
  GraduationCap,
  Sparkles,
  Target,
  CheckCircle2
} from "lucide-react";

const LearnPage = () => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [studiedTopics, setStudiedTopics] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subjectsData, progressData, recsData] = await Promise.all([
          api.get("/learn/subjects"),
          api.get("/learn/progress"),
          api.get("/learn/recommend")
        ]);
        setSubjects(subjectsData.subjects);
        setStudiedTopics(progressData.studied_topics || []);
        setRecommendations(recsData.recommendations || []);
      } catch (error) {
        console.error("Failed to fetch learn data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getSubjectProgress = (subject) => {
    const totalTopics = subject.topics?.length || 0;
    const completedTopics = subject.topics?.filter(t => 
      studiedTopics.includes(`${subject.id}/${t.id}`)
    ).length || 0;
    return totalTopics > 0 ? (completedTopics / totalTopics) * 100 : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading study materials...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="learn-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
          <GraduationCap className="w-8 h-8 text-primary" />
          Learn
        </h1>
        <p className="text-muted-foreground">Read short notes by topic, then test yourself.</p>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="glass rounded-2xl p-6 border border-primary/20 bg-primary/5">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
            <h2 className="font-semibold">Recommended for You</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {recommendations.slice(0, 3).map((rec, idx) => (
              <Link
                key={`${rec.subject_id}/${rec.topic_id}`}
                to={`/learn/${rec.subject_id}/${rec.topic_id}`}
                data-testid={`recommendation-card-${idx}`}
                className="block text-left p-4 rounded-xl bg-secondary border border-border hover:border-primary/50 transition-all hover-lift"
              >
                <div className="flex items-center gap-2 mb-1">
                  {rec.priority === "high" && (
                    <Target className="w-4 h-4 text-destructive" />
                  )}
                  <span className="text-xs text-muted-foreground">{rec.subject_name}</span>
                </div>
                <p className="font-medium text-sm">{rec.topic_name}</p>
                <p className="text-xs text-muted-foreground mt-1">{rec.reason}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Subject Grid */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Study by Subject</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {subjects.map((subject) => {
            const progress = getSubjectProgress(subject);
            const completedCount = subject.topics?.filter(t => 
              studiedTopics.includes(`${subject.id}/${t.id}`)
            ).length || 0;
            
            return (
              <div
                key={subject.id}
                className="glass rounded-2xl p-6 border border-border hover:border-border transition-all"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${subject.color}20` }}
                    >
                      <BookOpen className="w-6 h-6" style={{ color: subject.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold">{subject.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {subject.topic_count} topics
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium">{Math.round(progress)}%</span>
                    <p className="text-xs text-muted-foreground">{completedCount}/{subject.topic_count}</p>
                  </div>
                </div>

                <Progress value={progress} className="h-2 mb-4" />

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {subject.description}
                </p>

                {/* Topics List */}
                <div className="space-y-2">
                  {subject.topics?.slice(0, 3).map((topic) => {
                    const isCompleted = studiedTopics.includes(`${subject.id}/${topic.id}`);
                    return (
                      <button
                        key={topic.id}
                        onClick={() => navigate(`/learn/${subject.id}/${topic.id}`)}
                        className="w-full flex items-center justify-between p-3 rounded-lg bg-secondary hover:bg-secondary transition-all text-left"
                      >
                        <div className="flex items-center gap-3">
                          {isCompleted ? (
                            <CheckCircle2 className="w-4 h-4 text-success" />
                          ) : (
                            <div className="w-4 h-4 rounded-full border border-border" />
                          )}
                          <span className="text-sm">{topic.name}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {topic.estimated_time}m
                        </div>
                      </button>
                    );
                  })}
                </div>

                {subject.topics?.length > 3 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full mt-3"
                    onClick={() => navigate(`/learn/${subject.id}`)}
                  >
                    View all {subject.topic_count} topics
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LearnPage;
